/**
 * pastoralReasoningEngine.js
 *
 * Detects pastoral patterns from real tool result data.
 * Pure function — no side effects, no external deps.
 *
 * Detects:
 * - Abandono pastoral (people without recent visit)
 * - Oraciones críticas (prayers unresolved 14+ days)
 * - Seguimiento atrasado (next_visit_date overdue)
 * - Saturación ministerial (many pending visits)
 * - Iglesias inactivas (no recent activity)
 * - Pipeline bautismal (catechumens present)
 */

const PRAYER_CRITICAL_DAYS   = 14;
const VISIT_SATURATION_COUNT = 8;
const CHURCH_INACTIVE_DAYS   = 21;

function daysSince(dateStr) {
  if (!dateStr) return Infinity;
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return Infinity;
  return Math.floor((Date.now() - d.getTime()) / 86_400_000);
}

/**
 * @typedef {{ type: string, severity: "high"|"medium"|"low", insight: string, suggestion: string }} PastoralInsight
 */

/**
 * Detect pastoral patterns from raw tool data.
 *
 * @param {Record<string, Object[]>} dataByTool
 * @returns {PastoralInsight[]}
 */
export function detectPastoralPatterns(dataByTool) {
  const insights = [];

  // ── Abandono pastoral ─────────────────────────────────────────────
  const followupPeople = dataByTool["get_people_needing_followup"] ?? [];
  if (followupPeople.length > 0) {
    const n     = followupPeople.length;
    const names = followupPeople.slice(0, 2).map((p) => p.name).filter(Boolean).join(", ");
    insights.push({
      type:       "abandono_pastoral",
      severity:   n >= 5 ? "high" : n >= 2 ? "medium" : "low",
      insight:    `${n} persona${n !== 1 ? "s" : ""} sin visita reciente${names ? `: ${names}` : ""}.`,
      suggestion: "Programar visitas de seguimiento prioritario.",
    });
  }

  // ── Oraciones críticas ────────────────────────────────────────────
  const prayers = dataByTool["get_recent_prayers"] ?? [];
  const criticalPrayers = prayers.filter(
    (p) => p.status !== "respondida" && daysSince(p.created_at) >= PRAYER_CRITICAL_DAYS
  );
  if (criticalPrayers.length > 0) {
    const n = criticalPrayers.length;
    insights.push({
      type:       "oracion_critica",
      severity:   n >= 3 ? "high" : "medium",
      insight:    `${n} oración${n !== 1 ? "es" : ""} sin respuesta en más de ${PRAYER_CRITICAL_DAYS} días.`,
      suggestion: "Dar seguimiento a solicitudes de oración prolongadas.",
    });
  }

  // ── Seguimiento atrasado ─────────────────────────────────────────
  const allVisits = [
    ...(dataByTool["get_today_visits"]    ?? []),
    ...(dataByTool["get_upcoming_visits"] ?? []),
    ...(dataByTool["get_next_visit"]      ?? []),
  ];
  const uniqueVisits = [...new Map(allVisits.map((v) => [v.id, v])).values()];
  const overdue = uniqueVisits.filter(
    (v) => v.next_visit_date && v.status !== "realizada" && daysSince(v.next_visit_date) > 0
  );
  if (overdue.length > 0) {
    const n = overdue.length;
    insights.push({
      type:       "seguimiento_atrasado",
      severity:   "high",
      insight:    `${n} compromiso${n !== 1 ? "s" : ""} de seguimiento vencido${n !== 1 ? "s" : ""}.`,
      suggestion: "Revisar y reprogramar seguimientos atrasados.",
    });
  }

  // ── Saturación ministerial ────────────────────────────────────────
  const upcomingVisits = dataByTool["get_upcoming_visits"] ?? [];
  const pendingCount   = upcomingVisits.filter((v) => v.status === "pendiente").length;
  if (pendingCount >= VISIT_SATURATION_COUNT) {
    insights.push({
      type:       "saturacion_ministerial",
      severity:   "medium",
      insight:    `${pendingCount} visitas pendientes — carga pastoral alta esta semana.`,
      suggestion: "Priorizar o redistribuir visitas pendientes.",
    });
  }

  // ── Iglesias inactivas ────────────────────────────────────────────
  const churches         = dataByTool["get_church_activity_summary"] ?? [];
  const inactiveChurches = churches.filter(
    (c) => daysSince(c.last_activity_date) >= CHURCH_INACTIVE_DAYS
  );
  if (inactiveChurches.length > 0) {
    const n     = inactiveChurches.length;
    const names = inactiveChurches.slice(0, 2).map((c) => c.name).filter(Boolean).join(", ");
    insights.push({
      type:       "iglesia_inactiva",
      severity:   n >= 2 ? "high" : "medium",
      insight:    `${n} iglesia${n !== 1 ? "s" : ""} sin actividad reciente${names ? `: ${names}` : ""}.`,
      suggestion: "Visitar iglesias con menor actividad ministerial.",
    });
  }

  // ── Pipeline bautismal ────────────────────────────────────────────
  const members     = dataByTool["get_member_status"] ?? [];
  const catechumens = members.filter((m) => m.baptism_status === "catecumeno");
  if (catechumens.length >= 3) {
    insights.push({
      type:       "pipeline_bautismal",
      severity:   "low",
      insight:    `${catechumens.length} catecúmenos activos — potencial bautismal.`,
      suggestion: "Acompañar proceso de preparación bautismal.",
    });
  }

  const ORDER = { high: 0, medium: 1, low: 2 };
  return insights.sort((a, b) => (ORDER[a.severity] ?? 2) - (ORDER[b.severity] ?? 2));
}
